import { Injectable } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

export interface DownloadStatus {
    id: string;
    filename: string;
    progress: number;
    state: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';
    error?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DownloadService {
    private readonly activeDownloads = new BehaviorSubject<DownloadStatus[]>([]);
    readonly activeDownloads$ = this.activeDownloads.asObservable();

    constructor() { }

    download(request: Observable<HttpEvent<Blob>>, filename: string) {
        const id = Math.random().toString(36).substring(7);
        const downloadItem: DownloadStatus = {
            id,
            filename,
            progress: 0,
            state: 'PENDING'
        };

        this.updateDownloads([...this.activeDownloads.value, downloadItem]);

        request.subscribe({
            next: (event: HttpEvent<Blob>) => {
                this.handleHttpEvent(id, event, filename);
            },
            error: (err) => {
                this.updateDownloadItem(id, { state: 'ERROR', error: 'Download failed' });
                setTimeout(() => this.removeDownload(id), 2000);
            }
        });
    }

    private handleHttpEvent(id: string, event: HttpEvent<Blob>, filename: string) {
        switch (event.type) {
            case HttpEventType.DownloadProgress: {
                const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
                this.updateDownloadItem(id, { progress, state: 'IN_PROGRESS' });
                break;
            }

            case HttpEventType.Response:
                this.saveFile(event.body!, filename);
                this.updateDownloadItem(id, { progress: 100, state: 'COMPLETED' });
                setTimeout(() => this.removeDownload(id), 2000);
                break;
        }
    }

    private saveFile(blob: Blob, filename: string) {
        const link = document.createElement('a');
        link.href = globalThis.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            link.remove();
            globalThis.URL.revokeObjectURL(link.href);
        }, 100);
    }

    private updateDownloadItem(id: string, updates: Partial<DownloadStatus>) {
        const downloads = this.activeDownloads.value.map(item =>
            item.id === id ? { ...item, ...updates } : item
        );
        this.updateDownloads(downloads);
    }

    private removeDownload(id: string) {
        const downloads = this.activeDownloads.value.filter(item => item.id !== id);
        this.updateDownloads(downloads);
    }

    private updateDownloads(downloads: DownloadStatus[]) {
        this.activeDownloads.next(downloads);
    }
}
